import { IChatMessage, IChatRole, IChatTextPart } from './chatbot.types';

// Monotonic per-session id — uniqueness within the unpersisted panel session is
// all we need, so a counter beats crypto.randomUUID() (no React 16 polyfill risk).
let nextMessageId = 0;
export const newMessageId = (): string => `m${++nextMessageId}`;

export const textPart = (text: string): IChatTextPart => ({ type: 'text', text });

export const chatMessage = (role: IChatRole, text: string): IChatMessage => ({
  id: newMessageId(),
  role,
  content: [textPart(text)],
});

// Flatten structured content to a plain string — the adapter yields cumulative
// text deltas and the rendering layer only needs the joined string.
export const messageText = (message: IChatMessage): string =>
  message.content.map((part) => (part.type === 'text' ? part.text : '')).join('');

// Wire history is `[{ role, content }]` with content as a plain string (the FastAPI
// ChatRequest.history schema); structure is stripped here at the wire boundary.
export const toWireHistory = (
  messages: readonly IChatMessage[]
): { role: 'user' | 'assistant'; content: string }[] =>
  messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({ role: m.role as 'user' | 'assistant', content: messageText(m) }));

// Starter prompts the user hasn't asked yet. Match is case-insensitive and
// trim-tolerant, so a typed question counts the same as a clicked chip.
export const remainingStarters = (
  messages: readonly IChatMessage[],
  starters: readonly string[]
): readonly string[] => {
  const used = new Set(
    messages
      .filter((m) => m.role === 'user')
      .map((m) => messageText(m).trim().toLowerCase())
  );
  return starters.filter((s) => !used.has(s.trim().toLowerCase()));
};
